/* eslint-disable camelcase */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { Dispatch } from 'redux';

import { getCSRFToken, ROOT_URL_V2 } from '../../../../config';
import PopupHeader from '../../../common/PopupHeader';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { fetchClientAddressList } from '../../../../actions/ClientData';
import { AgentShiftForm, TJob, TRoleExtras } from '../AgentShiftForm';
import actions from '../../../../actions';
import history from '../../../../history';
import { getAllSkills, getErrorMessages, reduceDuplicatedKeyValPairs } from '../../../../utils';
import { TServiceKey, TServicesStructure } from '../../../../types';
import { TRootState } from '../../../../reducers';

export type TEditShiftContainerProps = {
    match: {
        params: {
            jobId: number;
        };
    };
    fetchClientAddressList: (data: any) => void;
    handleFormSubmit: (formData: any) => void;

    notification: {
        success?: (title: string, message: string, time?: string) => void;
    };
    load: (searchParams: any) => void;
    servicesStructure: TServicesStructure;
};

export type TEditShiftContainerState = {
    isFetching: boolean;
    job: TJob;
    roleExtras: TRoleExtras;
    initialService: TServiceKey;
    isLoading: boolean;
    errorMessage: string;
};

class EditShiftContainer extends Component<
    TEditShiftContainerProps,
    TEditShiftContainerState
> {
    constructor(props: TEditShiftContainerProps, context: any) {
        super(props, context);

        this.state = {
            isFetching: true,
            job: null,
            roleExtras: null,
            initialService: null,
            isLoading: false,
            errorMessage: null,
        };
    }

    componentDidMount() {
        const { jobId } = this.props.match.params;
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}jobs/${jobId}/`,
        })
            .then(response => {
                const jobData = response.data.content;
                if (!!jobData.is_editable || !!jobData.managed) {
                    const job: TJob = {
                        date: jobData.date,
                        duration: jobData.duration,
                        service: jobData.service,
                        additional_services: jobData.additional_services,
                        number_of_freelancers: jobData.number_of_freelancers,
                        start_time: moment(jobData.start_time, 'HH:mm:ss').format('HH:mm'),
                        freelancer_pay_per_hour: jobData.freelancer_pay_per_hour,
                        client_pay_per_hour: jobData.client_pay_per_hour,
                        address1: jobData.address1,
                        address2: jobData.address2,
                        postcode: jobData.postcode,
                        city: jobData.city,
                        payment_method: jobData.payment_method,
                        matching_mode: jobData.matching_mode,
                        breaks: jobData.breaks,
                        consultant_splits: jobData.consultant_splits,
                        client: jobData.client_full.id,
                        freelancers_information: jobData.freelancers_information,
                        admin_information: jobData.admin_information,
                        nature_of_business: jobData.nature_of_business,
                        required_qualifications: jobData.required_qualifications,
                        safety_risks: jobData.safety_risks,
                        available_facilities: jobData.available_facilities,
                        expenses_payable: jobData.expenses_payable,
                        title: jobData.title,
                        client_full: jobData.client_full,
                        managed: jobData.managed,
                        owned: jobData.owned,
                        rates: jobData.rates,
                        dbs_check: jobData.dbs_check,
                        qualifying_questions: jobData.qualifying_questions,
                        series: jobData.series,
                    };
                    const roleExtras = getAllSkills(this.props.servicesStructure)
                        .reduce((result: {[key: string]: any}, skill) => ({
                            ...result,
                            [skill]: jobData[skill],
                        }), {});
                    roleExtras.role = jobData.role;
                    roleExtras.vehicle_type = jobData.vehicle_type;

                    this.setState({
                        isFetching: false,
                        initialService: jobData.service,
                        job,
                        roleExtras,
                    });
                } else {
                    history.push('/jobs/list');
                }
            })
            .catch(response => {
                // eslint-disable-next-line no-console
                console.log(response);
            });
    }

    setErrorMessage = (errorMsg: string) => this.setState({ errorMessage: errorMsg });

    onDismiss = () => {
        // @ts-ignore
        this.props.fetchClientAddressList([]);
        const currentUrlParams = new URLSearchParams(history.location.search);
        history.push({
            pathname: '/jobs/list',
            search: currentUrlParams.toString(),
        });
    };

    handleSubmit = (formValues: TJob) => {
        this.setState({ isLoading: true });
        const { jobId } = this.props.match.params;
        const data = reduceDuplicatedKeyValPairs(
            formValues, { ...this.state.job, ...this.state.roleExtras }
        );

        axios({
            method: 'patch',
            url: `${ROOT_URL_V2}jobs/${jobId}/`,
            data,
            headers: {
                'X-CSRFToken': getCSRFToken(),
            },
        })
            .then(response => {
                const currentUrlParams = new URLSearchParams(history.location.search);
                const { reference_number } = response.data.content;

                currentUrlParams.set('search', reference_number);
                currentUrlParams.set('getall', '1');
                currentUrlParams.delete('after');
                currentUrlParams.delete('before');

                this.props.notification.success(
                    'Success',
                    `${reference_number} successfully edited`,
                );

                history.push({
                    pathname: '/jobs/list',
                    search: currentUrlParams.toString(),
                });
                this.props.load(currentUrlParams);
            })
            .catch(error => {
                this.setState({ isLoading: false });
                this.setErrorMessage(getErrorMessages(error));
            });
    };

    render() {
        return (
            <div className="create-shift">
                <PopupHeader title="&nbsp;" onDismiss={this.onDismiss} />
                {!this.state.isFetching ? (
                    <AgentShiftForm
                        defaultJob={this.state.job}
                        handleFormSubmit={this.handleSubmit}
                        mode="edit"
                        isLoading={this.state.isLoading}
                        errorMessage={this.state.errorMessage}
                        setErrorMessage={this.setErrorMessage}
                        roleExtras={this.state.roleExtras}
                        initialService={this.state.initialService}
                    />
                ) : (
                    <LoadingSpinner
                        className="shift-spinner"
                        backgroundColor="white"
                    />
                )}
            </div>
        );
    }
}


const mapStateToProps = (state: TRootState) => ({
    servicesStructure: state.servicesStructure,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    load: (searchParams: any) => dispatch(actions.loadShifts(searchParams)),
    fetchClientAddressList: (data: any) => dispatch(fetchClientAddressList(data)),
});

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(EditShiftContainer);
